/**
 * THE one definition of what a PO / Indent is worth, frontend side.
 * Mirrors backend/src/services/po-totals.ts exactly — keep the two in step.
 *
 * WHY: an audit found eight different formulas across the app, so one purchase
 * order showed a different number in almost every place it appeared. PO-IN801-1
 * (goods ₹55,200, 26% discount −₹14,352, GST ₹7,352.64) rendered as ₹55,200 in
 * the PO list, ₹55,200 in the Vendor & Pricing panel, ₹48,200.64 in the items
 * footer, and ₹55,200 on the printed copy the VENDOR receives — with the
 * negotiated discount nowhere on the document.
 *
 * THE RULE
 *   net = goods − discount + gst + transport
 *
 * GST is charged on the post-discount base (the backend computes gstAmount that
 * way), so discount comes off BEFORE gst is added.
 *
 * NOTE: `totalAmount` from the API is GOODS ONLY and deliberately stays that
 * way — approval brackets and every analytics rollup depend on that meaning.
 * Always display `net`, never raw `totalAmount`.
 */

const n = (v: unknown): number => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

export interface PoTotals {
  goods: number;
  discount: number;
  gst: number;
  transport: number;
  /** goods − discount + gst + transport. THE payable figure. */
  net: number;
  /** True when anything beyond goods is present — drives "show the breakdown". */
  hasBreakup: boolean;
}

/**
 * Compute a PO's money breakdown from whatever shape the API returned.
 *
 * Prefers `netAmount` when the server sent it (it is computed by the same
 * formula), then the stored doc-level rollups, and finally sums `items[]`.
 *
 * The `?? undefined` guard matters: a genuine stored 0 must NOT fall through to
 * the line sum. `Number(x) || fallback` treated a real zero as missing, which is
 * exactly how a deliberate 0% GST turned back into a summed GST figure.
 */
export function poTotals(po: any): PoTotals {
  const items: any[] = Array.isArray(po?.items) ? po.items : [];
  const sumLines = (pick: (it: any) => number) => items.reduce((s, it) => s + n(pick(it)), 0);
  const prefer = (stored: unknown, pick: (it: any) => number) =>
    stored === null || stored === undefined ? sumLines(pick) : n(stored);

  const goods = prefer(po?.totalAmount, (it) => n(it.amount) || n(it.quantity) * n(it.rate));
  const discount = prefer(po?.discountTotal, (it) => n(it.discountAmount));
  const gst = prefer(po?.gstTotal, (it) => n(it.gstAmount));
  const transport = prefer(po?.transportTotal, (it) => n(it.transportCost));

  // Trust a server-sent netAmount when present — it is authoritative for
  // sheet-blended rows whose line items never reach the client.
  const net =
    po?.netAmount === null || po?.netAmount === undefined
      ? round2(goods - discount + gst + transport)
      : n(po.netAmount);

  return {
    goods: round2(goods),
    discount: round2(discount),
    gst: round2(gst),
    transport: round2(transport),
    net: round2(net),
    hasBreakup: discount > 0 || gst > 0 || transport > 0,
  };
}

/** Just the payable figure — the common case for a table cell. */
export const poNet = (po: any): number => poTotals(po).net;

/**
 * "Goods ₹55,200 − Discount ₹14,352 + GST ₹7,353 = ₹48,201"
 * for tooltips and one-line summaries, so a number is never unexplained.
 */
export function poTotalsSummary(po: any): string {
  const t = poTotals(po);
  const f = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;
  if (!t.hasBreakup) return f(t.net);
  const parts = [`Goods ${f(t.goods)}`];
  if (t.discount > 0) parts.push(`− Discount ${f(t.discount)}`);
  if (t.gst > 0) parts.push(`+ GST ${f(t.gst)}`);
  if (t.transport > 0) parts.push(`+ Transport ${f(t.transport)}`);
  return `${parts.join(' ')} = ${f(t.net)}`;
}

const round2 = (v: number): number => Math.round(v * 100) / 100;
